import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, CheckCircle2, AlertCircle, FileText, Shield } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { DocumentWatermark } from "@/components/DocumentWatermark";
import { getDocumentVersion, initializeDocument, updateDocumentVersion } from "@/lib/documentVersion";

interface AVVChecklistItem {
  id: string;
  label: string;
  completed: boolean;
}

interface AVVProvider {
  id: string;
  name: string;
  category: string;
  purpose: string;
  status: "completed" | "pending" | "review";
  avvUrl: string;
  dataLocation: string;
  thirdCountry: boolean;
  contractType: string;
  checklist: AVVChecklistItem[];
  notes: string;
  contactInfo?: {
    company: string;
    address: string;
    email?: string;
  };
}

const AdminAuftragsverarbeitung = () => {
  const { toast } = useToast();
  const [providers, setProviders] = useState<AVVProvider[]>([]);
  const [docVersion, setDocVersion] = useState(getDocumentVersion('avv-uebersicht'));

  useEffect(() => {
    const version = initializeDocument('avv-uebersicht', '1.0');
    setDocVersion(version);
  }, []);

  const initialProviders: AVVProvider[] = [
    {
      id: "google-analytics",
      name: "Google Analytics 4",
      category: "Website-Analyse",
      purpose: "Erfassung und Analyse von Website-Nutzungsdaten zur Optimierung der Benutzererfahrung",
      status: "completed",
      avvUrl: "https://business.safety.google/adsprocessorterms/",
      dataLocation: "EU und USA",
      thirdCountry: true,
      contractType: "Standard Contract Clauses (SCC)",
      notes: "IP-Anonymisierung aktiviert. Datenaufbewahrung auf 14 Monate begrenzt.",
      contactInfo: {
        company: "Google Ireland Limited",
        address: "Gordon House, Barrow Street, Dublin 4, Ireland",
        email: "support-deutschland@google.com"
      },
      checklist: [
        { id: "ga-1", label: "AVV online unterzeichnet", completed: true },
        { id: "ga-2", label: "Standard Contract Clauses (SCC) akzeptiert", completed: true },
        { id: "ga-3", label: "IP-Anonymisierung aktiviert", completed: true },
        { id: "ga-4", label: "Datenaufbewahrung auf 14 Monate konfiguriert", completed: true },
        { id: "ga-5", label: "Datenweitergabe an Google deaktiviert", completed: true },
        { id: "ga-6", label: "Keine Verknüpfung mit Google Ads", completed: true },
        { id: "ga-7", label: "Cookie-Banner implementiert (Opt-in)", completed: true },
        { id: "ga-8", label: "Datenschutzerklärung aktualisiert", completed: true }
      ]
    },
    {
      id: "supabase",
      name: "Supabase (Lovable Cloud)",
      category: "Backend-Infrastruktur",
      purpose: "Hosting der Datenbank, Authentifizierung und Backend-Services",
      status: "completed",
      avvUrl: "https://supabase.com/legal/dpa",
      dataLocation: "EU (Frankfurt)",
      thirdCountry: false,
      contractType: "Data Processing Agreement (DPA)",
      notes: "EU-Server in Frankfurt. Verschlüsselte Datenspeicherung. DSGVO-konform.",
      contactInfo: {
        company: "Supabase Inc.",
        address: "340 S Lemon Ave #7968, Walnut, CA 91789, USA"
      },
      checklist: [
        { id: "sb-1", label: "DPA akzeptiert und unterzeichnet", completed: true },
        { id: "sb-2", label: "EU-Region (Frankfurt) ausgewählt", completed: true },
        { id: "sb-3", label: "SSL-Verschlüsselung aktiviert", completed: true },
        { id: "sb-4", label: "Row-Level-Security (RLS) implementiert", completed: true },
        { id: "sb-5", label: "Backup-Strategie dokumentiert", completed: true },
        { id: "sb-6", label: "Zugriffskontrolle konfiguriert", completed: true },
        { id: "sb-7", label: "Datenschutzerklärung aktualisiert", completed: true }
      ]
    },
    {
      id: "world4you",
      name: "World4You (SMTP E-Mail-Versand)",
      category: "E-Mail-Services",
      purpose: "Versand von Terminbestätigungen, Kontaktformular-Benachrichtigungen und automatisierten E-Mails",
      status: "completed",
      avvUrl: "https://www.world4you.com/de/agb/avv.html",
      dataLocation: "Österreich / EU",
      thirdCountry: false,
      contractType: "Auftragsverarbeitungsvertrag (AVV)",
      notes: "EU-Server in Österreich. DSGVO-konformer E-Mail-Versand über SMTP. Nur transaktionale E-Mails, kein Marketing.",
      contactInfo: {
        company: "World4You Internet Services GmbH",
        address: "Hafenstraße 35, 4020 Linz, Österreich",
        email: "office@world4you.com"
      },
      checklist: [
        { id: "w4y-1", label: "AVV mit Hosting-Vertrag abgeschlossen", completed: true },
        { id: "w4y-2", label: "EU-Server (Österreich) gewählt", completed: true },
        { id: "w4y-3", label: "Nur transaktionale E-Mails aktiviert", completed: true },
        { id: "w4y-4", label: "SSL/TLS-Verschlüsselung (Port 587) aktiviert", completed: true },
        { id: "w4y-5", label: "Keine Marketing-E-Mails ohne Einwilligung", completed: true },
        { id: "w4y-6", label: "Datenschutzerklärung aktualisiert", completed: true }
      ]
    },
    {
      id: "google-fonts",
      name: "Google Fonts (Lokal gehostet)",
      category: "Website-Darstellung",
      purpose: "Bereitstellung von Web-Schriftarten (Lato) - lokal gehostet",
      status: "completed",
      avvUrl: "https://policies.google.com/terms/service-specific",
      dataLocation: "Lokal (eigener Server)",
      thirdCountry: false,
      contractType: "Kein AVV erforderlich (lokales Hosting)",
      notes: "✅ DSGVO-konform: Schriftarten werden lokal gehostet. Keine Datenübermittlung an Google. Alle benötigten Lato-Schriftschnitte (300, 400, 400i, 600i, 700, 900) sind lokal verfügbar.",
      contactInfo: {
        company: "Nicht zutreffend",
        address: "Schriftarten werden vom eigenen Server geladen"
      },
      checklist: [
        { id: "gf-1", label: "Schriftarten lokal heruntergeladen (WOFF2)", completed: true },
        { id: "gf-2", label: "Font-CSS lokal erstellt", completed: true },
        { id: "gf-3", label: "Alle benötigten Schriftschnitte verfügbar", completed: true },
        { id: "gf-4", label: "Keine externe Verbindung zu Google", completed: true },
        { id: "gf-5", label: "font-display: swap für Performance", completed: true },
        { id: "gf-6", label: "Datenschutzerklärung aktualisiert", completed: true }
      ]
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      category: "Social Media",
      purpose: "Bereitstellung von Social Media Links (keine Datenverarbeitung über LinkedIn-Dienste)",
      status: "completed",
      avvUrl: "https://legal.linkedin.com/dpa",
      dataLocation: "Nur externe Links, keine Datenübertragung",
      thirdCountry: false,
      contractType: "Kein AVV erforderlich (nur externe Verlinkung)",
      notes: "✅ DSGVO-konform: Es werden nur externe Links zu LinkedIn bereitgestellt. Keine Einbindung von LinkedIn-Tracking-Pixel, Plugins oder anderen Diensten. Beim Klick auf den Link verlässt der Nutzer unsere Website und wird zu LinkedIn weitergeleitet.",
      contactInfo: {
        company: "Nicht zutreffend",
        address: "Nur externe Verlinkung"
      },
      checklist: [
        { id: "li-1", label: "Kein LinkedIn Insight Tag implementiert", completed: true },
        { id: "li-2", label: "Keine LinkedIn-Plugins eingebunden", completed: true },
        { id: "li-3", label: "Nur externe Verlinkung (target='_blank')", completed: true },
        { id: "li-4", label: "Keine Datenübermittlung an LinkedIn", completed: true },
        { id: "li-5", label: "Datenschutzerklärung aktualisiert", completed: true }
      ]
    },
    {
      id: "youtube",
      name: "YouTube",
      category: "Social Media",
      purpose: "Bereitstellung von Social Media Links (keine Datenverarbeitung über YouTube-Dienste)",
      status: "completed",
      avvUrl: "https://policies.google.com/terms/service-specific",
      dataLocation: "Nur externe Links, keine Datenübertragung",
      thirdCountry: false,
      contractType: "Kein AVV erforderlich (nur externe Verlinkung)",
      notes: "✅ DSGVO-konform: Es werden nur externe Links zu YouTube bereitgestellt. Keine Einbindung von YouTube-Videos, Tracking-Pixel oder anderen Diensten. Beim Klick auf den Link verlässt der Nutzer unsere Website und wird zu YouTube weitergeleitet.",
      contactInfo: {
        company: "Nicht zutreffend",
        address: "Nur externe Verlinkung"
      },
      checklist: [
        { id: "yt-1", label: "Keine YouTube-Videos eingebettet", completed: true },
        { id: "yt-2", label: "Kein YouTube-Tracking implementiert", completed: true },
        { id: "yt-3", label: "Nur externe Verlinkung (target='_blank')", completed: true },
        { id: "yt-4", label: "Keine Datenübermittlung an YouTube", completed: true },
        { id: "yt-5", label: "Datenschutzerklärung aktualisiert", completed: true }
      ]
    }
  ];

  useEffect(() => {
    const saved = localStorage.getItem("avv-providers");
    if (saved) {
      setProviders(JSON.parse(saved));
    } else {
      setProviders(initialProviders);
    }
  }, []);

  const toggleChecklistItem = (providerId: string, itemId: string) => {
    const updated = providers.map(provider => {
      if (provider.id === providerId) {
        return {
          ...provider,
          checklist: provider.checklist.map(item =>
            item.id === itemId ? { ...item, completed: !item.completed } : item
          )
        };
      }
      return provider;
    });
    setProviders(updated);
    localStorage.setItem("avv-providers", JSON.stringify(updated));
    
    // Update document version
    const updatedVersion = updateDocumentVersion('avv-uebersicht', {
      author: 'Admin',
      description: `Checkliste aktualisiert: ${providerId}`
    });
    setDocVersion(updatedVersion);
    
    toast({
      title: "Status aktualisiert",
      description: "Checkliste wurde gespeichert.",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500/10 text-green-700 border-green-500/20";
      case "review":
        return "bg-yellow-500/10 text-yellow-700 border-yellow-500/20";
      case "pending":
        return "bg-red-500/10 text-red-700 border-red-500/20";
      default:
        return "bg-secondary";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return "Abgeschlossen";
      case "review":
        return "In Prüfung";
      case "pending":
        return "Ausstehend";
      default:
        return status;
    }
  };

  const completedProviders = providers.filter(p => p.status === "completed").length;
  const totalProviders = providers.length;
  const completionPercentage = Math.round((completedProviders / totalProviders) * 100);

  const downloadAVVOverview = () => {
    window.print();
    toast({
      title: "Druckansicht geöffnet",
      description: "Speichern Sie die Seite als PDF über Ihren Browser.",
    });
  };

  const resetToDefaults = () => {
    setProviders(initialProviders);
    localStorage.setItem("avv-providers", JSON.stringify(initialProviders));
    toast({
      title: "Zurückgesetzt",
      description: "Alle AVV-Daten wurden auf Standardwerte zurückgesetzt.",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="max-w-full overflow-x-hidden pt-24 pb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <Breadcrumbs items={[
            { label: "Admin" },
            { label: "Dokumente", href: "/admin/documents" },
            { label: "Auftragsverarbeitung" }
          ]} />
          
          
          <DocumentWatermark 
            documentName="Auftragsverarbeitungsverträge (AVV)"
            version={docVersion}
          />

          <div className="mb-8 flex flex-col items-start justify-between gap-4 mt-6 lg:flex-row">
            <div>
              <h1 className="break-words text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                Auftragsverarbeitungsverträge
              </h1>
              <p className="text-xl text-muted-foreground">
                Dokumentation gemäß Art. 28 DSGVO
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={resetToDefaults} className="flex items-center gap-2">
                Zurücksetzen
              </Button>
              <Button onClick={downloadAVVOverview} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Als PDF speichern
              </Button>
            </div>
          </div>

          {/* Progress Overview */}
          <Card className="p-4 sm:p-6 mb-8 border-2 border-primary/20">
            <div className="flex items-start gap-4">
              <div className="shrink-0 p-3 bg-primary/10 rounded-lg">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  AVV-Fortschritt
                </h2>
                <p className="text-muted-foreground mb-4">
                  {completedProviders} von {totalProviders} Auftragsverarbeiter vollständig dokumentiert ({completionPercentage}%)
                </p>
                <div className="w-full bg-secondary h-3 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto mb-8 p-1 [scrollbar-width:thin]">
              <TabsTrigger value="overview" className="shrink-0">Übersicht</TabsTrigger>
              <TabsTrigger value="signing" className="shrink-0">Unterzeichnung</TabsTrigger>
              <TabsTrigger value="details" className="shrink-0">Details & Checklisten</TabsTrigger>
              <TabsTrigger value="compliance" className="shrink-0">Compliance-Hinweise</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-2 gap-6">
                {providers.map(provider => {
                  const completed = provider.checklist.filter(item => item.completed).length;
                  const total = provider.checklist.length;
                  const progress = Math.round((completed / total) * 100);

                  return (
                    <Card key={provider.id} className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="min-w-0">
                          <h3 className="text-xl font-semibold text-foreground mb-1">
                            {provider.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">{provider.category}</p>
                        </div>
                        <Badge className={`shrink-0 ${getStatusColor(provider.status)}`}>
                          {getStatusLabel(provider.status)}
                        </Badge>
                      </div>

                      <p className="text-sm text-muted-foreground mb-4">{provider.purpose}</p>

                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Speicherort:</span>
                          <span className="font-medium text-foreground">{provider.dataLocation}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Drittland:</span>
                          <span className={`font-medium ${provider.thirdCountry ? "text-yellow-600" : "text-green-600"}`}>
                            {provider.thirdCountry ? "Ja" : "Nein"}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Vertragsart:</span>
                          <span className="font-medium text-foreground">{provider.contractType}</span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Checkliste</span>
                          <span className="font-medium text-foreground">{completed}/{total}</span>
                        </div>
                        <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-500 ${
                              progress === 100 ? "bg-green-500" : "bg-primary"
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => window.open(provider.avvUrl, "_blank")}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        AVV aufrufen
                      </Button>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Signing Instructions Tab */}
            <TabsContent value="signing">
              <Card className="p-6 mb-6 border-2 border-primary/20 bg-primary/5">
                <div className="flex items-start gap-4">
                  <FileText className="w-8 h-8 text-primary flex-shrink-0" />
                  <div>
                    <h2 className="text-2xl font-semibold text-foreground mb-2">
                      Schritt-für-Schritt-Anleitung zur AVV-Unterzeichnung
                    </h2>
                    <p className="text-muted-foreground">
                      Folgen Sie diesen Anleitungen, um die Auftragsverarbeitungsverträge online zu unterzeichnen.
                    </p>
                  </div>
                </div>
              </Card>

              <div className="space-y-6">
                {/* Google Analytics AVV */}
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                    Google Analytics 4 - AVV unterzeichnen
                  </h3>
                  <div className="space-y-4 ml-10">
                    <div className="space-y-2">
                      <p className="font-medium text-foreground">Was Sie benötigen:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        <li>Google-Konto mit Zugriff auf Google Analytics</li>
                        <li>Admin-Berechtigung für Ihre GA4-Property</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <p className="font-medium text-foreground">Anleitung:</p>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Gehen Sie zu <a href="https://business.safety.google/adsprocessorterms/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Ads Data Processing Terms</a></li>
                        <li>Melden Sie sich mit Ihrem Google-Konto an</li>
                        <li>Lesen Sie die Bedingungen sorgfältig durch</li>
                        <li>Klicken Sie auf "Akzeptieren" am Ende der Seite</li>
                        <li>Speichern Sie die Bestätigung (Screenshot oder PDF)</li>
                        <li>Notieren Sie das Datum der Annahme</li>
                      </ol>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-lg">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        <strong>Wichtig:</strong> Die Annahme gilt für alle Google Analytics Properties Ihres Kontos. Dokumentieren Sie Datum und Uhrzeit der Annahme für Ihre Unterlagen.
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <Button 
                        className="flex-1"
                        onClick={() => window.open("https://business.safety.google/adsprocessorterms/", "_blank")}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Jetzt Google AVV öffnen
                      </Button>
                      <Button 
                        variant="outline"
                        className="flex-1"
                        asChild
                      >
                        <a href="/downloads/google-ads-avv.pdf" download="Google-Ads-AVV.pdf">
                          <Download className="w-4 h-4 mr-2" />
                          AVV als PDF
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>


                {/* Supabase DPA */}
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                    Supabase (Lovable Cloud) - DPA unterzeichnen
                  </h3>
                  <div className="space-y-4 ml-10">
                    <div className="space-y-2">
                      <p className="font-medium text-foreground">Was Sie benötigen:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        <li>Supabase Account (über Lovable Cloud bereitgestellt)</li>
                        <li>Zugriff auf Ihr Supabase Dashboard</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <p className="font-medium text-foreground">Anleitung:</p>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Gehen Sie zur <a href="https://supabase.com/legal/dpa" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Supabase DPA Seite</a></li>
                        <li>Lesen Sie die Datenschutzvereinbarung sorgfältig durch</li>
                        <li>Laden Sie das DPA-Dokument herunter</li>
                        <li>Bei Bedarf: Kontaktieren Sie Supabase für eine unterzeichnete Version</li>
                        <li>Stellen Sie sicher, dass Ihre Daten in der EU-Region (Frankfurt) gespeichert werden</li>
                      </ol>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 p-4 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        <strong>Vorteil:</strong> Lovable Cloud nutzt Supabase mit EU-Servern (Frankfurt). Keine Drittland-Übermittlung erforderlich!
                      </p>
                    </div>
                    <Button 
                      className="w-full"
                      onClick={() => window.open("https://supabase.com/legal/dpa", "_blank")}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Supabase DPA öffnen
                    </Button>
                  </div>
                </Card>

                {/* World4You SMTP */}
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                    World4You - AVV prüfen
                  </h3>
                  <div className="space-y-4 ml-10">
                    <div className="space-y-2">
                      <p className="font-medium text-foreground">Was Sie benötigen:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        <li>World4You Hosting-Konto</li>
                        <li>Zugriff auf Ihre Vertragsunterlagen</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <p className="font-medium text-foreground">Anleitung:</p>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Der AVV ist normalerweise Teil Ihres Hosting-Vertrags mit World4You</li>
                        <li>Prüfen Sie Ihre Vertragsunterlagen auf den Auftragsverarbeitungsvertrag</li>
                        <li>Falls nicht vorhanden, kontaktieren Sie den World4You Support</li>
                        <li>Laden Sie eine Kopie des AVV herunter</li>
                        <li>Dokumentieren Sie das Datum des Vertragsabschlusses</li>
                      </ol>
                    </div>
                    <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 p-4 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        <strong>Vorteil:</strong> World4You betreibt Server in Österreich (EU). Keine Drittland-Übermittlung!
                      </p>
                    </div>
                    <Button 
                      className="w-full"
                      onClick={() => window.open("https://www.world4you.com/de/agb/avv.html", "_blank")}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      World4You AVV-Informationen
                    </Button>
                  </div>
                </Card>

                {/* General Checklist after signing */}
                <Card className="p-6 border-2 border-green-500/20 bg-green-500/5">
                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    Nach der Unterzeichnung - Wichtige Schritte
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Dokumentation speichern</p>
                        <p className="text-sm text-muted-foreground">Speichern Sie Screenshots oder PDF-Kopien aller akzeptierten AVVs</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Datum notieren</p>
                        <p className="text-sm text-muted-foreground">Dokumentieren Sie das Datum und die Uhrzeit der Annahme jedes AVV</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Checkliste abhaken</p>
                        <p className="text-sm text-muted-foreground">Gehen Sie zum Tab "Details & Checklisten" und haken Sie die entsprechenden Punkte ab</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Verarbeitungsverzeichnis aktualisieren</p>
                        <p className="text-sm text-muted-foreground">Aktualisieren Sie Ihr <a href="/admin/verarbeitungsverzeichnis" className="text-primary hover:underline">Verarbeitungsverzeichnis</a> mit den AVV-Details</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Jährliche Überprüfung einplanen</p>
                        <p className="text-sm text-muted-foreground">Setzen Sie eine Erinnerung für die jährliche Überprüfung aller AVVs</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details">
              <div className="space-y-8">
                {providers.map(provider => (
                  <Card key={provider.id} className="p-6">
                    <div className="border-b border-border pb-4 mb-6">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h2 className="text-2xl font-bold text-foreground">{provider.name}</h2>
                          <p className="text-muted-foreground">{provider.category}</p>
                        </div>
                        <Badge className={getStatusColor(provider.status)}>
                          {getStatusLabel(provider.status)}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          VERARBEITUNGSZWECK
                        </h3>
                        <p className="text-foreground">{provider.purpose}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          KONTAKTINFORMATIONEN
                        </h3>
                        {provider.contactInfo && (
                          <div className="text-sm space-y-1">
                            <p className="font-semibold text-foreground">{provider.contactInfo.company}</p>
                            <p className="text-muted-foreground">{provider.contactInfo.address}</p>
                            {provider.contactInfo.email && (
                              <p className="text-primary">{provider.contactInfo.email}</p>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          DATENSPEICHERORT
                        </h3>
                        <p className="text-foreground">{provider.dataLocation}</p>
                        {provider.thirdCountry && (
                          <p className="text-yellow-600 text-sm mt-1">
                            ⚠️ Datenübermittlung in Drittland
                          </p>
                        )}
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                          VERTRAGSGRUNDLAGE
                        </h3>
                        <p className="text-foreground">{provider.contractType}</p>
                        <a 
                          href={provider.avvUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary text-sm hover:underline inline-flex items-center gap-1 mt-1"
                        >
                          AVV online aufrufen <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>

                    <div className="bg-secondary/30 p-4 rounded-lg mb-6">
                      <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                        HINWEISE
                      </h3>
                      <p className="text-sm text-foreground">{provider.notes}</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-4">
                        COMPLIANCE-CHECKLISTE
                      </h3>
                      <div className="space-y-3">
                        {provider.checklist.map(item => (
                          <div 
                            key={item.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                              item.completed 
                                ? "bg-green-500/5 border-green-500/20" 
                                : "bg-secondary/30 border-border"
                            }`}
                          >
                            <Checkbox
                              id={item.id}
                              checked={item.completed}
                              onCheckedChange={() => toggleChecklistItem(provider.id, item.id)}
                              className="mt-0.5"
                            />
                            <label 
                              htmlFor={item.id}
                              className={`text-sm cursor-pointer flex-1 ${
                                item.completed ? "line-through text-muted-foreground" : "text-foreground"
                              }`}
                            >
                              {item.label}
                            </label>
                            {item.completed && (
                              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Compliance Tab */}
            <TabsContent value="compliance">
              <div className="space-y-6">
                <Card className="p-6 border-2 border-primary/20">
                  <div className="flex items-start gap-4">
                    <FileText className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h2 className="text-xl font-semibold text-foreground mb-3">
                        Rechtliche Anforderungen an AVV
                      </h2>
                      <div className="space-y-3 text-sm text-muted-foreground">
                        <p>
                          Gemäß <strong className="text-foreground">Art. 28 DSGVO</strong> muss jeder Auftragsverarbeitungsvertrag 
                          folgende Mindestangaben enthalten:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                          <li>Gegenstand und Dauer der Verarbeitung</li>
                          <li>Art und Zweck der Verarbeitung</li>
                          <li>Art der personenbezogenen Daten</li>
                          <li>Kategorien betroffener Personen</li>
                          <li>Pflichten und Rechte des Verantwortlichen</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border-2 border-yellow-500/20">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                    <div>
                      <h2 className="text-xl font-semibold text-foreground mb-3">
                        Datenübermittlung in Drittländer
                      </h2>
                      <div className="space-y-3 text-sm text-muted-foreground">
                        <p>
                          Bei Datenübermittlung in Länder außerhalb der EU/EWR (z.B. USA) müssen zusätzliche 
                          Garantien vorhanden sein:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-4">
                          <li>
                            <strong className="text-foreground">Standard Contract Clauses (SCC)</strong> - 
                            Von der EU-Kommission genehmigte Vertragsklauseln
                          </li>
                          <li>
                            <strong className="text-foreground">Angemessenheitsbeschluss</strong> - 
                            EU-Kommission bestätigt angemessenes Datenschutzniveau (aktuell nur für wenige Länder)
                          </li>
                          <li>
                            <strong className="text-foreground">Binding Corporate Rules</strong> - 
                            Verbindliche interne Datenschutzvorschriften für Unternehmensgruppen
                          </li>
                        </ul>
                        <p className="mt-3 text-yellow-700 font-medium">
                          ⚠️ Für USA-Transfers: Prüfen Sie die Gültigkeit der Standard Contract Clauses und 
                          dokumentieren Sie zusätzliche Schutzmaßnahmen.
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border-2 border-green-500/20">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h2 className="text-xl font-semibold text-foreground mb-3">
                        Best Practices
                      </h2>
                      <div className="space-y-3 text-sm text-muted-foreground">
                        <ul className="list-disc list-inside space-y-2 ml-4">
                          <li>Führen Sie ein zentrales Register aller Auftragsverarbeiter</li>
                          <li>Dokumentieren Sie alle AVVs schriftlich (auch bei Online-Services)</li>
                          <li>Prüfen Sie regelmäßig (mindestens jährlich) die Aktualität der AVVs</li>
                          <li>Fordern Sie bei Drittanbietern Nachweise über technische und organisatorische Maßnahmen (TOMs) an</li>
                          <li>Vereinbaren Sie Kontrollrechte zur Überprüfung der Einhaltung</li>
                          <li>Klären Sie Verantwortlichkeiten bei Datenschutzverletzungen</li>
                          <li>Definieren Sie Prozesse für Betroffenenanfragen</li>
                          <li>Legen Sie Löschfristen und Rückgaberechte fest</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Häufige Fragen zu AVV
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        Brauche ich einen AVV für jeden Cloud-Dienst?
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Ja, sobald personenbezogene Daten an einen Dienstleister übermittelt werden, 
                        der diese in Ihrem Auftrag verarbeitet, ist ein AVV erforderlich.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        Sind Online-AVVs rechtlich bindend?
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Ja, viele große Anbieter (Google, Meta, etc.) bieten rechtlich bindende 
                        Online-AVVs an. Dokumentieren Sie Datum und Zeitpunkt der Annahme.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">
                        Was passiert bei Verstößen?
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Fehlende oder unzureichende AVVs können zu Bußgeldern bis zu 10 Mio. € oder 
                        2% des weltweiten Jahresumsatzes führen (Art. 83 Abs. 4 DSGVO).
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminAuftragsverarbeitung;
